<!-- PropertyDetailsModal.vue -->
<template>
  <div class="modal fade details-modal" tabindex="-1" role="dialog" aria-labelledby="propertyDetailsLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title text-uppercase fw-bold" id="propertyDetailsLabel">
            <i class="mdi mdi-home-outline me-2"></i>Property Details - Plot {{ property.LRNumber }}
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Property Status Banner -->
          <div class="alert mb-0 rounded-0 border-0" :class="getStatusBannerClass()">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-1 fw-bold">
                  <i :class="getStatusIcon()"></i>
                  Status: {{ property.status || 'Active' }}
                </h6>
                <small v-if="property.status === 'Suspended' && property.suspensionReason">
                  Reason: {{ property.suspensionReason }}
                </small>
                <small v-if="property.status === 'Rate Struck' && property.rateStruckReason">
                  Reason: {{ property.rateStruckReason }}
                </small>
              </div>
              <div class="text-end">
                <div class="fw-bold">Total Balance</div>
                <div class="h5 mb-0" :class="property.currentBalance > 0 ? 'text-danger' : 'text-success'">
                  KES {{ numberFormat(property.currentBalance || property.outstandingArrears || 0) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Tabbed Content -->
          <ul class="nav nav-tabs nav-justified" id="propertyTabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button class="nav-link active fw-semibold" id="basic-tab" data-bs-toggle="tab" data-bs-target="#basic" type="button" role="tab">
                <i class="mdi mdi-information-outline me-1"></i>Basic Info
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link fw-semibold" id="owner-tab" data-bs-toggle="tab" data-bs-target="#owner" type="button" role="tab">
                <i class="mdi mdi-account-outline me-1"></i>Owner Details
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link fw-semibold" id="financial-tab" data-bs-toggle="tab" data-bs-target="#financial" type="button" role="tab">
                <i class="mdi mdi-currency-usd me-1"></i>Financial
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link fw-semibold" id="history-tab" data-bs-toggle="tab" data-bs-target="#history" type="button" role="tab">
                <i class="mdi mdi-history me-1"></i>History
              </button>
            </li>
          </ul>

          <div class="tab-content p-4" id="propertyTabsContent">
            <!-- Basic Information Tab -->
            <div class="tab-pane fade show active" id="basic" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-map-marker me-2"></i>Property Information
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">UPN Number</label>
                        <div class="fw-bold text-primary fs-6">{{ property.UPN || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">LR/Plot Number</label>
                        <div class="fw-bold">{{ property.LRNumber || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Physical Address</label>
                        <div>{{ property.percelAddress || property.physicalAddress || 'Not specified' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Parcel Size</label>
                        <div>
                          <span v-if="property.percelSize && property.percelSize > 0">
                            {{ numberFormat(property.percelSize) }} Ha
                          </span>
                          <span v-else class="text-muted">Not specified</span>
                        </div>
                      </div>

                      <div class="info-item mb-0">
                        <label class="text-muted small fw-semibold">Land Use</label>
                        <div>{{ property.useName || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-map me-2"></i>Location Details
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Sub County</label>
                        <div class="fw-bold">{{ property.subCountyName || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Ward</label>
                        <div>{{ property.wardName || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Zone</label>
                        <div>{{ property.zone || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-0">
                        <label class="text-muted small fw-semibold">Block</label>
                        <div>{{ property.block || 'N/A' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Owner Details Tab -->
            <div class="tab-pane fade" id="owner" role="tabpanel">
              <div class="row">
                <!-- Primary Owner -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-account me-2"></i>Primary Owner
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Name</label>
                        <div class="fw-bold">{{ property.ownerNames || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">ID Number</label>
                        <div>{{ property.ownerID || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Phone Number</label>
                        <div>
                          <span v-if="property.ownerPhoneNo">
                            <i class="mdi mdi-phone text-success me-1"></i>
                            {{ maskPhoneNumber(property.ownerPhoneNo) }}
                          </span>
                          <span v-else class="text-muted">Not provided</span>
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Email Address</label>
                        <div>
                          <span v-if="property.ownerEmail">
                            <i class="mdi mdi-email text-primary me-1"></i>
                            {{ property.ownerEmail }}
                          </span>
                          <span v-else class="text-muted">Not provided</span>
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">PIN Number</label>
                        <div>{{ property.pinNumber || 'N/A' }}</div>
                      </div>

                      <div class="info-item mb-0">
                        <label class="text-muted small fw-semibold">Postal Address</label>
                        <div>
                          <span v-if="property.boxNo || property.postalCode || property.town">
                            P.O. Box {{ property.boxNo || 'N/A' }}-{{ property.postalCode || 'N/A' }} {{ property.town || 'N/A' }}
                          </span>
                          <span v-else class="text-muted">Not provided</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Co-Owner Details -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-account-multiple me-2"></i>Co-Owner
                      </h6>
                    </div>
                    <div class="card-body">
                      <div v-if="property.coOwnerNames">
                        <div class="info-item mb-3">
                          <label class="text-muted small fw-semibold">Name</label>
                          <div class="fw-bold">{{ property.coOwnerNames }}</div>
                        </div>

                        <div class="info-item mb-3">
                          <label class="text-muted small fw-semibold">ID Number</label>
                          <div>{{ property.coOwnerID || 'N/A' }}</div>
                        </div>

                        <div class="info-item mb-3">
                          <label class="text-muted small fw-semibold">Phone Number</label>
                          <div>
                            <span v-if="property.coOwnerPhoneNo">
                              <i class="mdi mdi-phone text-success me-1"></i>
                              {{ maskPhoneNumber(property.coOwnerPhoneNo) }}
                            </span>
                            <span v-else class="text-muted">Not provided</span>
                          </div>
                        </div>

                        <div class="info-item mb-3">
                          <label class="text-muted small fw-semibold">Email Address</label>
                          <div>
                            <span v-if="property.coOwnerEmail">
                              <i class="mdi mdi-email text-primary me-1"></i>
                              {{ property.coOwnerEmail }}
                            </span>
                            <span v-else class="text-muted">Not provided</span>
                          </div>
                        </div>

                        <div class="info-item mb-0">
                          <label class="text-muted small fw-semibold">Postal Address</label>
                          <div>
                            <span v-if="property.coOwnerBoxNo || property.coOwnerPostalCode || property.coOwnerTown">
                              P.O. Box {{ property.coOwnerBoxNo || 'N/A' }}-{{ property.coOwnerPostalCode || 'N/A' }} {{ property.coOwnerTown || 'N/A' }}
                            </span>
                            <span v-else class="text-muted">Not provided</span>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center text-muted py-4">
                        <i class="mdi mdi-account-off display-6 opacity-50"></i>
                        <p class="mb-0 mt-2">No co-owner information available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Tab -->
            <div class="tab-pane fade" id="financial" role="tabpanel">
              <div class="row">
                <!-- Annual Charges -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-success">
                        <i class="mdi mdi-currency-usd me-2"></i>Annual Charges
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row text-center">
                        <div class="col-6">
                          <div class="border-end">
                            <div class="text-muted small">Land Rates</div>
                            <div class="h5 fw-bold text-success mb-0">
                              KES {{ numberFormat(property.annualLandRates || 0) }}
                            </div>
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="text-muted small">Ground Rent</div>
                          <div class="h5 fw-bold text-info mb-0">
                            KES {{ numberFormat(property.annualGroundRent || 0) }}
                          </div>
                        </div>
                      </div>
                      <hr>
                      <div class="text-center">
                        <div class="text-muted small">Total Annual Charges</div>
                        <div class="h4 fw-bold text-primary mb-0">
                          KES {{ numberFormat(property.annualCharges || (parseFloat(property.annualLandRates || 0) + parseFloat(property.annualGroundRent || 0))) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Outstanding & Arrears -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-danger">
                        <i class="mdi mdi-alert-circle me-2"></i>Outstanding Amounts
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Land Rates Arrears</label>
                        <div class="fw-bold text-danger">
                          KES {{ numberFormat(property.landRatesArrears || 0) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Ground Rent Arrears</label>
                        <div class="fw-bold text-danger">
                          KES {{ numberFormat(property.groundRentArrears || 0) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Accumulated Penalty</label>
                        <div class="fw-bold text-warning">
                          KES {{ numberFormat(property.accumPenalty || 0) }}
                        </div>
                      </div>

                      <div class="info-item mb-0">
                        <label class="text-muted small fw-semibold">Accumulated Interest</label>
                        <div class="fw-bold text-warning">
                          KES {{ numberFormat(property.accumInterest || 0) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Summary -->
              <div class="row mt-4">
                <div class="col-12">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-calculator me-2"></i>Payment Summary
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row text-center">
                        <div class="col-md-3">
                          <div class="border-end">
                            <div class="text-muted small">Total Payments</div>
                            <div class="h5 fw-bold text-primary mb-0">
                              KES {{ numberFormat(property.payments || 0) }}
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="border-end">
                            <div class="text-muted small">Outstanding Arrears</div>
                            <div class="h5 fw-bold text-danger mb-0">
                              KES {{ numberFormat(property.outstandingArrears || 0) }}
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="border-end">
                            <div class="text-muted small">Current Balance</div>
                            <div class="h5 fw-bold mb-0" :class="property.currentBalance > 0 ? 'text-danger' : 'text-success'">
                              KES {{ numberFormat(property.currentBalance || 0) }}
                            </div>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="text-muted small">Payment Status</div>
                          <div class="h6 mb-0">
                            <span v-if="(property.currentBalance || property.outstandingArrears) <= 0" class="badge bg-success">
                              <i class="mdi mdi-check-circle me-1"></i>Paid Up
                            </span>
                            <span v-else class="badge bg-danger">
                              <i class="mdi mdi-alert-circle me-1"></i>Outstanding
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- History Tab -->
            <div class="tab-pane fade" id="history" role="tabpanel">
              <div class="row">
                <!-- Status History -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-timeline me-2"></i>Status History
                      </h6>
                    </div>
                    <div class="card-body">
                      <!-- Creation Info -->
                      <div class="timeline-item mb-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="timeline-icon bg-success">
                              <i class="mdi mdi-plus"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1 fw-bold">Property Created</h6>
                            <p class="text-muted small mb-1">
                              Added by {{ property.createdBy }} (ID: {{ property.createdByIDNo }})
                            </p>
                            <small class="text-muted">{{ formatDate(property.dateCreated) }}</small>
                          </div>
                        </div>
                      </div>

                      <!-- Suspension History -->
                      <div v-if="property.dateSuspended" class="timeline-item mb-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="timeline-icon bg-danger">
                              <i class="mdi mdi-lock"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1 fw-bold">Property Suspended</h6>
                            <p class="text-muted small mb-1">
                              Suspended by {{ property.suspendedBy }}
                            </p>
                            <p class="text-muted small mb-1">
                              Reason: {{ property.suspensionReason || 'Not specified' }}
                            </p>
                            <small class="text-muted">{{ formatDate(property.dateSuspended) }}</small>
                          </div>
                        </div>
                      </div>

                      <!-- Reinstatement History -->
                      <div v-if="property.dateReinstated" class="timeline-item mb-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="timeline-icon bg-success">
                              <i class="mdi mdi-lock-open-outline"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1 fw-bold">Property Reinstated</h6>
                            <p class="text-muted small mb-1">
                              Reinstated by {{ property.reinstatedBy }}
                            </p>
                            <small class="text-muted">{{ formatDate(property.dateReinstated) }}</small>
                          </div>
                        </div>
                      </div>

                      <!-- Rate Struck History -->
                      <div v-if="property.dateRateStruck" class="timeline-item mb-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="timeline-icon bg-warning">
                              <i class="mdi mdi-currency-usd-off"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1 fw-bold">Marked as Rate Struck</h6>
                            <p class="text-muted small mb-1">
                              Marked by {{ property.rateStruckBy }}
                            </p>
                            <p class="text-muted small mb-1">
                              Reason: {{ property.rateStruckReason || 'Not specified' }}
                            </p>
                            <small class="text-muted">{{ formatDate(property.dateRateStruck) }}</small>
                          </div>
                        </div>
                      </div>

                      <!-- Rate Struck Removal -->
                      <div v-if="property.dateRateStruckRemoved" class="timeline-item mb-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="timeline-icon bg-success">
                              <i class="mdi mdi-currency-usd"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1 fw-bold">Rate Struck Removed</h6>
                            <p class="text-muted small mb-1">
                              Removed by {{ property.rateStruckRemovedBy }}
                            </p>
                            <small class="text-muted">{{ formatDate(property.dateRateStruckRemoved) }}</small>
                          </div>
                        </div>
                      </div>

                      <div v-if="!property.dateSuspended && !property.dateReinstated && !property.dateRateStruck && !property.dateRateStruckRemoved" class="text-center text-muted py-4">
                        <i class="mdi mdi-history display-6 opacity-50"></i>
                        <p class="mb-0 mt-2">No status changes recorded</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Additional Information -->
                <div class="col-md-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0 fw-bold text-primary">
                        <i class="mdi mdi-information me-2"></i>Additional Information
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Property ID</label>
                        <div class="fw-bold">{{ property.id }}</div>
                      </div>

                      <div v-if="property.parent_property_id" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Parent Property ID</label>
                        <div>{{ property.parent_property_id }}</div>
                      </div>

                      <div v-if="property.subdivision_reference" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Subdivision Reference</label>
                        <div>{{ property.subdivision_reference }}</div>
                      </div>

                      <div v-if="property.amalgamation_reference" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Amalgamation Reference</label>
                        <div>{{ property.amalgamation_reference }}</div>
                      </div>

                      <div v-if="property.uploadBatchReference" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Upload Batch Reference</label>
                        <div>{{ property.uploadBatchReference }}</div>
                      </div>

                      <div v-if="property.uploadDate" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Upload Date</label>
                        <div>{{ formatDate(property.uploadDate) }}</div>
                      </div>

                      <div v-if="property.updatedBy" class="info-item mb-3">
                        <label class="text-muted small fw-semibold">Last Updated By</label>
                        <div>{{ property.updatedBy }}</div>
                      </div>

                      <div v-if="property.updatedDate" class="info-item mb-0">
                        <label class="text-muted small fw-semibold">Last Updated Date</label>
                        <div>{{ formatDate(property.updatedDate) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="mdi mdi-close me-1"></i>Close
          </button>
          <button type="button" class="btn btn-primary" @click="printDetails">
            <i class="mdi mdi-printer me-1"></i>Print Details
          </button>
          <button type="button" class="btn btn-outline-primary" @click="printDetailsAlternative">
            <i class="mdi mdi-printer-outline me-1"></i>Print (Alternative)
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PropertyDetailsModal',
  props: {
    property: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A';

      let d = new Date(date);
      let day = d.getDate();
      let month = d.toLocaleString('en-US', { month: 'short' });
      let year = d.getFullYear();

      let suffix = '';
      if (day % 10 === 1 && day !== 11) {
        suffix = 'st';
      } else if (day % 10 === 2 && day !== 12) {
        suffix = 'nd';
      } else if (day % 10 === 3 && day !== 13) {
        suffix = 'rd';
      } else {
        suffix = 'th';
      }
      return `${day}${suffix} ${month} ${year}`;
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num || 0);
    },

    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') {
        return input;
      }

      const digits = input.replace(/\D/g, '');

      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }

      return input;
    },

    getStatusBannerClass() {
      const status = this.property.status || 'Active';
      switch (status) {
        case 'Active':
          return 'alert-success';
        case 'Suspended':
          return 'alert-danger';
        case 'Rate Struck':
          return 'alert-warning';
        case 'Subdivided':
          return 'alert-info';
        default:
          return 'alert-secondary';
      }
    },

    getStatusIcon() {
      const status = this.property.status || 'Active';
      switch (status) {
        case 'Active':
          return 'mdi mdi-check-circle text-success me-2';
        case 'Suspended':
          return 'mdi mdi-lock text-danger me-2';
        case 'Rate Struck':
          return 'mdi mdi-currency-usd-off text-warning me-2';
        case 'Subdivided':
          return 'mdi mdi-shape-rectangle-plus text-info me-2';
        default:
          return 'mdi mdi-information text-secondary me-2';
      }
    },

    getStatusBorderColor() {
      const status = this.property.status || 'Active';
      switch (status) {
        case 'Active':
          return '#28a745';
        case 'Suspended':
          return '#dc3545';
        case 'Rate Struck':
          return '#ffc107';
        case 'Subdivided':
          return '#17a2b8';
        default:
          return '#6c757d';
      }
    },

    printDetails() {
      try {
        // Generate the print content
        const printContent = this.generatePrintContent();

        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');

        if (!printWindow) {
          alert('Please allow pop-ups for this site to enable printing.');
          return;
        }

        // Write the content to the new window
        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();

        // Wait for images and content to load, then print
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();

            // Optional: Close window after printing
            printWindow.onafterprint = function() {
              setTimeout(() => {
                printWindow.close();
              }, 500);
            };
          }, 500);
        };

        // Fallback for browsers that don't trigger onload
        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            printWindow.focus();
            printWindow.print();
          }
        }, 1000);

      } catch (error) {
        console.error('Print error:', error);
        alert('There was an error generating the print document. Please try again.');
      }
    },

    printDetailsAlternative() {
      try {
        // Create a hidden div with the print content
        const printContent = this.generatePrintContent();

        // Create a temporary div
        const printDiv = document.createElement('div');
        printDiv.innerHTML = printContent;
        printDiv.style.display = 'none';

        // Add to body
        document.body.appendChild(printDiv);

        // Store current body content
        const originalContent = document.body.innerHTML;

        // Replace body content with print content
        document.body.innerHTML = printDiv.innerHTML;

        // Print
        window.print();

        // Restore original content
        document.body.innerHTML = originalContent;

        // Clean up
        if (document.body.contains(printDiv)) {
          document.body.removeChild(printDiv);
        }

        // Re-initialize Vue if needed (this is a workaround)
        this.$nextTick(() => {
          // The modal might need to be reopened after body content is restored
          console.log('Print completed - alternative method');
        });

      } catch (error) {
        console.error('Alternative print error:', error);
        alert('Both print methods failed. Please try copying the content or contact support.');
      }
    },

    generatePrintContent() {
      const currentDate = new Date().toLocaleDateString('en-GB');
      const currentTime = new Date().toLocaleTimeString('en-GB');

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Property Details - Plot ${this.property.LRNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.6;
              color: #333;
              background: white;
            }

            .print-container {
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }

            .header {
              text-align: center;
              border-bottom: 2px solid #495057;
              padding-bottom: 20px;
              margin-bottom: 30px;
              background: #f8f9fa;
              padding: 25px;
              border-radius: 8px;
            }

            .header h1 {
              color: #495057;
              font-size: 24px;
              margin-bottom: 8px;
              font-weight: 600;
            }

            .header .subtitle {
              color: #6c757d;
              font-size: 16px;
              font-weight: 500;
            }

            .status-banner {
              background: #f8f9fa;
              border: 2px solid ${this.getStatusBorderColor()};
              color: #495057;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 30px;
              text-align: center;
            }

            .status-banner h3 {
              margin-bottom: 10px;
              color: ${this.getStatusBorderColor()};
            }

            .section {
              margin-bottom: 30px;
              break-inside: avoid;
            }

            .section-title {
              background: #495057;
              color: white;
              padding: 12px 20px;
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 20px;
              border-radius: 6px;
            }

            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 20px;
            }

            .info-block {
              border: 1px solid #e9ecef;
              border-radius: 6px;
              overflow: hidden;
            }

            .info-block-header {
              background: #6c757d;
              color: white;
              padding: 12px 15px;
              font-weight: 600;
              font-size: 14px;
              border-bottom: 1px solid #dee2e6;
            }

            .info-block-content {
              padding: 15px;
            }

            .info-item {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #f0f0f0;
            }

            .info-item:last-child {
              border-bottom: none;
            }

            .info-label {
              font-weight: 600;
              color: #6c757d;
              min-width: 140px;
            }

            .info-value {
              color: #495057;
              text-align: right;
              flex: 1;
            }

            .financial-summary {
              background: #f8f9fa;
              border: 1px solid #dee2e6;
              border-radius: 8px;
              padding: 25px;
              margin: 20px 0;
            }

            .financial-summary h4 {
              color: #495057;
              text-align: center;
              margin-bottom: 20px;
              font-size: 18px;
              font-weight: 600;
            }

            .balance-highlight {
              text-align: center;
              font-size: 18px;
              font-weight: 600;
              color: ${(this.property.currentBalance || this.property.outstandingArrears || 0) > 0 ? '#6c757d' : '#495057'};
              background: ${(this.property.currentBalance || this.property.outstandingArrears || 0) > 0 ? '#fff3cd' : '#d1edff'};
              padding: 12px;
              border-radius: 6px;
              border: 1px solid ${(this.property.currentBalance || this.property.outstandingArrears || 0) > 0 ? '#ffeaa7' : '#b3d7ff'};
            }

            .timeline {
              margin-top: 15px;
            }

            .timeline-item {
              border-left: 2px solid #6c757d;
              margin-left: 15px;
              padding-left: 20px;
              margin-bottom: 20px;
              position: relative;
            }

            .timeline-item::before {
              content: '';
              position: absolute;
              left: -7px;
              top: 0;
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background: #6c757d;
            }

            .timeline-date {
              font-size: 12px;
              color: #6c757d;
            }

            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e9ecef;
              text-align: center;
              color: #6c757d;
              font-size: 12px;
            }

            @media print {
              body {
                margin: 0;
                font-size: 12pt;
                line-height: 1.4;
              }

              .print-container {
                padding: 0;
                max-width: none;
              }

              .section {
                page-break-inside: avoid;
                margin-bottom: 20px;
              }

              .info-grid {
                display: block;
              }

              .info-block {
                margin-bottom: 15px;
                break-inside: avoid;
              }

              .header {
                page-break-after: avoid;
              }

              .status-banner {
                page-break-after: avoid;
                page-break-inside: avoid;
              }

              .financial-summary {
                page-break-inside: avoid;
              }

              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <!-- Header -->
            <div class="header">
              <h1>PROPERTY DETAILS REPORT</h1>
              <div class="subtitle">
                Plot ${this.property.LRNumber || 'N/A'} | UPN: ${this.property.UPN || 'N/A'}
              </div>
            </div>

            <!-- Status Banner -->
            <div class="status-banner">
              <h3>Status: ${this.property.status || 'Active'}</h3>
              <div class="balance-highlight">
                Current Balance: KES ${this.numberFormat(this.property.currentBalance || this.property.outstandingArrears || 0)}
              </div>
              ${this.property.status === 'Suspended' && this.property.suspensionReason ?
          `<div style="margin-top: 10px; padding: 8px; background: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px; color: #721c24;">
                  <strong>Suspension Reason:</strong> ${this.property.suspensionReason}
                </div>` : ''}
              ${this.property.status === 'Rate Struck' && this.property.rateStruckReason ?
          `<div style="margin-top: 10px; padding: 8px; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; color: #856404;">
                  <strong>Rate Struck Reason:</strong> ${this.property.rateStruckReason}
                </div>` : ''}
            </div>

            <!-- Basic Property Information -->
            <div class="section">
              <div class="section-title">Basic Property Information</div>
              <div class="info-grid">
                <div class="info-block">
                  <div class="info-block-header">Property Details</div>
                  <div class="info-block-content">
                    <div class="info-item">
                      <span class="info-label">UPN Number:</span>
                      <span class="info-value">${this.property.UPN || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">LR/Plot Number:</span>
                      <span class="info-value">${this.property.LRNumber || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Physical Address:</span>
                      <span class="info-value">${this.property.percelAddress || this.property.physicalAddress || 'Not specified'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Parcel Size:</span>
                      <span class="info-value">${this.property.percelSize && this.property.percelSize > 0 ? this.numberFormat(this.property.percelSize) + ' Ha' : 'Not specified'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Land Use:</span>
                      <span class="info-value">${this.property.useName || 'Not specified'}</span>
                    </div>
                  </div>
                </div>

                <div class="info-block">
                  <div class="info-block-header">Location Details</div>
                  <div class="info-block-content">
                    <div class="info-item">
                      <span class="info-label">Sub County:</span>
                      <span class="info-value">${this.property.subCountyName || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Ward:</span>
                      <span class="info-value">${this.property.wardName || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Zone:</span>
                      <span class="info-value">${this.property.zone || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Block:</span>
                      <span class="info-value">${this.property.block || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Owner Information -->
            <div class="section">
              <div class="section-title">Owner Information</div>
              <div class="info-grid">
                <div class="info-block">
                  <div class="info-block-header">Primary Owner</div>
                  <div class="info-block-content">
                    <div class="info-item">
                      <span class="info-label">Name:</span>
                      <span class="info-value">${this.property.ownerNames || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">ID Number:</span>
                      <span class="info-value">${this.property.ownerID || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Phone:</span>
                      <span class="info-value">${this.property.ownerPhoneNo ? this.maskPhoneNumber(this.property.ownerPhoneNo) : 'Not provided'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Email:</span>
                      <span class="info-value">${this.property.ownerEmail || 'Not provided'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">PIN Number:</span>
                      <span class="info-value">${this.property.pinNumber || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                ${this.property.coOwnerNames ? `
                <div class="info-block">
                  <div class="info-block-header">Co-Owner</div>
                  <div class="info-block-content">
                    <div class="info-item">
                      <span class="info-label">Name:</span>
                      <span class="info-value">${this.property.coOwnerNames}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">ID Number:</span>
                      <span class="info-value">${this.property.coOwnerID || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Phone:</span>
                      <span class="info-value">${this.property.coOwnerPhoneNo ? this.maskPhoneNumber(this.property.coOwnerPhoneNo) : 'Not provided'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Email:</span>
                      <span class="info-value">${this.property.coOwnerEmail || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
                ` : '<div class="info-block"><div class="info-block-header">Co-Owner</div><div class="info-block-content"><p style="text-align: center; color: #6c757d; padding: 20px;">No co-owner information available</p></div></div>'}
              </div>
            </div>

            <!-- Financial Information -->
            <div class="section">
              <div class="section-title">Financial Information</div>

              <div class="financial-summary">
                <h4>Payment Summary</h4>
                <div class="info-grid">
                  <div>
                    <div class="info-item">
                      <span class="info-label">Annual Land Rates:</span>
                      <span class="info-value">KES ${this.numberFormat(this.property.annualLandRates || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Annual Ground Rent:</span>
                      <span class="info-value">KES ${this.numberFormat(this.property.annualGroundRent || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Total Annual Charges:</span>
                      <span class="info-value"><strong>KES ${this.numberFormat(this.property.annualCharges || (parseFloat(this.property.annualLandRates || 0) + parseFloat(this.property.annualGroundRent || 0)))}</strong></span>
                    </div>
                  </div>
                  <div>
                    <div class="info-item">
                      <span class="info-label">Land Rates Arrears:</span>
                      <span class="info-value" style="color: #856404; font-weight: 600;">KES ${this.numberFormat(this.property.landRatesArrears || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Ground Rent Arrears:</span>
                      <span class="info-value" style="color: #856404; font-weight: 600;">KES ${this.numberFormat(this.property.groundRentArrears || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Accumulated Penalty:</span>
                      <span class="info-value" style="color: #856404; font-weight: 600;">KES ${this.numberFormat(this.property.accumPenalty || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Accumulated Interest:</span>
                      <span class="info-value" style="color: #6c757d; font-weight: 600;">KES ${this.numberFormat(this.property.accumInterest || 0)}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Total Payments:</span>
                      <span class="info-value" style="color: #155724; font-weight: 600;">KES ${this.numberFormat(this.property.payments || 0)}</span>
                    </div>
                  </div>
                </div>
                <hr style="margin: 15px 0;">
                <div class="balance-highlight">
                  Current Balance: KES ${this.numberFormat(this.property.currentBalance || this.property.outstandingArrears || 0)}
                </div>
              </div>
            </div>

            <!-- History -->
            ${this.generateHistorySection()}

            <!-- Footer -->
            <div class="footer">
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #dee2e6;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <strong>Property Details Report</strong><br>
                  <small>Land Rate Management System</small>
                </div>
                <div style="text-align: right;">
                  <strong>Generated:</strong> ${currentDate} at ${currentTime}<br>
                  <small>Property ID: ${this.property.id || 'N/A'}</small>
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;
    },

    generateHistorySection() {
      let historyItems = [];

      // Creation
      if (this.property.dateCreated) {
        historyItems.push({
          title: 'Property Created',
          description: `Added by ${this.property.createdBy} (ID: ${this.property.createdByIDNo})`,
          date: this.property.dateCreated
        });
      }

      // Suspension
      if (this.property.dateSuspended) {
        historyItems.push({
          title: 'Property Suspended',
          description: `Suspended by ${this.property.suspendedBy}. Reason: ${this.property.suspensionReason || 'Not specified'}`,
          date: this.property.dateSuspended
        });
      }

      // Reinstatement
      if (this.property.dateReinstated) {
        historyItems.push({
          title: 'Property Reinstated',
          description: `Reinstated by ${this.property.reinstatedBy}`,
          date: this.property.dateReinstated
        });
      }

      // Rate Struck
      if (this.property.dateRateStruck) {
        historyItems.push({
          title: 'Marked as Rate Struck',
          description: `Marked by ${this.property.rateStruckBy}. Reason: ${this.property.rateStruckReason || 'Not specified'}`,
          date: this.property.dateRateStruck
        });
      }

      // Rate Struck Removal
      if (this.property.dateRateStruckRemoved) {
        historyItems.push({
          title: 'Rate Struck Removed',
          description: `Removed by ${this.property.rateStruckRemovedBy}`,
          date: this.property.dateRateStruckRemoved
        });
      }

      if (historyItems.length === 0) {
        return `
          <div class="section">
            <div class="section-title">Property History</div>
            <p style="text-align: center; color: #6c757d; padding: 40px;">No status changes recorded</p>
          </div>
        `;
      }

      const timelineHtml = historyItems.map(item => `
        <div class="timeline-item">
          <h4>${item.title}</h4>
          <p>${item.description}</p>
          <div class="timeline-date">${this.formatDate(item.date)}</div>
        </div>
      `).join('');

      return `
        <div class="section">
          <div class="section-title">Property History</div>
          <div class="timeline">
            ${timelineHtml}
          </div>
        </div>
      `;
    }
  }
}
</script>

<style scoped>
.info-item {
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.timeline-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.timeline-item:last-child {
  margin-bottom: 0 !important;
}

.nav-tabs .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  color: #6c757d;
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #495057;
  background-color: #f8f9fa;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: transparent;
  border-color: transparent transparent #007bff;
  font-weight: 600;
}

.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.modal-xl {
  max-width: 1200px;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }

  .nav-tabs .nav-link {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }
}
</style>